import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, ScanLine, Grid3x3, Circle, Download } from 'lucide-react';
import { csvToRecords, mergeCsvIntoEditUnits, templateCsv, slug } from './csvHelpers';
import { extractPdfTextTokens, detectRectsFromTokens, DEFAULT_CODE_PATTERN } from './pdfTextDetect';
import { ocrWords, detectRectsFromOcr } from './ocrDetect';
import { autoPlace } from './autoPlace';
import { rectToPolygon } from './rectToPolygon';

/**
 * ImportPanel — bulk authoring helper that lives in the Digital Twin editor.
 *
 * Three actions:
 *   1. CSV import — paste a leasing roster keyed by unit_code; matched edit
 *      units get tenant_name/sqft/status filled, unplaced rows surface for
 *      auto-place.
 *   2. Auto-detect — PDF text tokens (vector PDFs) or Tesseract OCR (rasters)
 *      → unit rectangles → polygons.
 *   3. Auto-place — drop oval-ring or grid boxes for any CSV row not yet on
 *      the plan.
 *
 * Props:
 *   editUnits       current state from TwinPage
 *   setEditUnits    setter (parent owns persistence)
 *   floorPlan       { url, format, width, height } | null
 *   onMerge(detectedUnits)   parent applies detections to its state
 */
export default function ImportPanel({ editUnits, setEditUnits, floorPlan, onMerge }) {
    const { t } = useTranslation();
    const csvRef = useRef(null);

    const [csv, setCsv] = useState(null);
    const [busy, setBusy] = useState('');
    const [pdfTokens, setPdfTokens] = useState(null);
    const [ocrWordList, setOcrWordList] = useState(null);
    const [ocr, setOcr] = useState(null);
    const [codePattern, setCodePattern] = useState(DEFAULT_CODE_PATTERN);
    const [minConf, setMinConf] = useState(35);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);

    const isPdf = floorPlan && (floorPlan.format === 'pdf' || /\.pdf($|\?)/i.test(floorPlan.url || ''));
    const isRaster = floorPlan && !isPdf;

    // ─── CSV ─────────────────────────────────────────────────────
    const handleCsv = async (file) => {
        if (!file) return;
        setError(null);
        try {
            const text = await file.text();
            const parsed = csvToRecords(text);
            if (!parsed.records.length) {
                setError(t('No rows with a unit code found. Make sure your CSV has a unit_code column.'));
                return;
            }
            setCsv(parsed);
            const { merged, unplaced } = mergeCsvIntoEditUnits(editUnits, parsed.records);
            setEditUnits(merged);
            setInfo(t('Loaded {{n}} CSV rows · {{m}} matched · {{u}} unplaced', {
                n: parsed.records.length,
                m: parsed.records.length - unplaced.length,
                u: unplaced.length,
            }));
        } catch (e) {
            setError(e.message || t('CSV parse failed'));
        }
    };

    const downloadTemplate = () => {
        const blob = new Blob([templateCsv()], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'egoal_units_template.csv';
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    };

    // ─── PDF auto-detect ─────────────────────────────────────────
    const runPdfDetect = useCallback(async () => {
        if (!floorPlan?.url) return;
        setBusy(t('Reading PDF…'));
        setError(null);
        try {
            const tokens = await extractPdfTextTokens(floorPlan.url);
            setPdfTokens(tokens);
            if (!tokens.length) {
                setError(t('PDF has no selectable text — try OCR instead.'));
                return;
            }
            const rects = detectRectsFromTokens(tokens, codePattern);
            applyDetectedRects(rects, t('Detected {{n}} units from PDF text labels', { n: rects.length }));
        } catch (e) {
            setError(e.message || t('PDF detection failed'));
        } finally {
            setBusy('');
        }
    }, [floorPlan, codePattern, t]);

    // ─── OCR ─────────────────────────────────────────────────────
    const runOcr = useCallback(async () => {
        if (!floorPlan?.url) return;
        setError(null);
        setOcr({ pct: 0, status: t('Starting OCR…') });
        try {
            const words = await ocrWords(floorPlan.url, setOcr);
            setOcrWordList(words);
            const rects = detectRectsFromOcr(words, codePattern, minConf);
            applyDetectedRects(rects, t('OCR found {{n}} unit codes', { n: rects.length }));
        } catch (e) {
            setError((e && e.message) || t('OCR failed'));
        } finally {
            setOcr(null);
        }
    }, [floorPlan, codePattern, minConf, t]);

    // ─── Re-scan (without re-running expensive OCR / PDF read) ───
    const rescan = useCallback(() => {
        setError(null);
        if (ocrWordList) {
            const rects = detectRectsFromOcr(ocrWordList, codePattern, minConf);
            applyDetectedRects(rects, t('Rescanned · {{n}} units', { n: rects.length }));
        } else if (pdfTokens) {
            const rects = detectRectsFromTokens(pdfTokens, codePattern);
            applyDetectedRects(rects, t('Rescanned · {{n}} units', { n: rects.length }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ocrWordList, pdfTokens, codePattern, minConf]);

    // ─── Auto-place unplaced CSV rows ───────────────────────────
    const placeUnplaced = (shape) => {
        if (!csv) return;
        const usedCodes = new Set(editUnits.map((u) => slug(u.unit_id)).filter(Boolean));
        const unplaced = csv.records.filter((r) => !usedCodes.has(slug(r.unit_code)));
        if (!unplaced.length) {
            setInfo(t('All CSV rows already placed.'));
            return;
        }
        const aspect = floorPlan?.width && floorPlan?.height ? (floorPlan.width / floorPlan.height) : 1.4;
        const rects = autoPlace(unplaced, aspect, shape);
        applyDetectedRects(rects, t('Auto-placed {{n}} units ({{shape}})', { n: rects.length, shape }));
    };

    // ─── Common: convert detected rects → editUnits polygons ────
    function applyDetectedRects(rects, message) {
        if (!rects || !rects.length) {
            setInfo(t('No matches — try widening the pattern or lowering confidence.'));
            return;
        }
        const detected = rects.map((r) => ({
            unit_id: r.code,
            tenant_id: '',
            tenant_name: '',
            floor: 1,
            zone: '',
            size_sqft: 0,
            status: 'vacant',
            polygon: rectToPolygon(r),
        }));
        if (onMerge) onMerge(detected);
        setInfo(message);
    }

    // ─── render ──────────────────────────────────────────────────
    return (
        <div className="v3-card v3-cc-card v3-import-panel">
            <h3>{t('Import & Auto-detect')}</h3>

            {(error || info) && (
                <div className={'v3-import-msg ' + (error ? 'v3-import-msg-err' : 'v3-import-msg-ok')}>
                    {error || info}
                </div>
            )}

            {/* CSV */}
            <div className="v3-import-section">
                <div className="v3-import-row">
                    <strong>{t('CSV roster')}</strong>
                    {csv && <span className="v3-text-muted">{t('{{n}} rows · {{c}} fields', { n: csv.records.length, c: csv.columns.length })}</span>}
                </div>
                <div className="v3-import-actions">
                    <input ref={csvRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => handleCsv(e.target.files?.[0])} />
                    <button className="v3-btn v3-btn-ghost" type="button" onClick={() => csvRef.current?.click()}>
                        <Upload size={14} /> {csv ? t('Replace CSV') : t('Upload CSV')}
                    </button>
                    <button className="v3-btn v3-btn-ghost" type="button" onClick={downloadTemplate}>
                        <Download size={14} /> {t('Template')}
                    </button>
                </div>
                {csv && (
                    <div className="v3-import-chips">
                        {csv.columns.map((c) => (<span key={c} className="v3-import-chip">{c}</span>))}
                    </div>
                )}
            </div>

            {/* Auto-detect (PDF or OCR) */}
            {floorPlan?.url && (
                <div className="v3-import-section">
                    <div className="v3-import-row">
                        <strong>{t('Auto-detect units')}</strong>
                        <span className="v3-text-muted">{isPdf ? t('vector PDF') : t('raster image')}</span>
                    </div>
                    <div className="v3-import-actions">
                        {isPdf && (
                            <button className="v3-btn v3-btn-ghost" type="button" onClick={runPdfDetect} disabled={!!busy || !!ocr}>
                                <ScanLine size={14} /> {busy || t('Detect from PDF text')}
                            </button>
                        )}
                        {isRaster && (
                            <button className="v3-btn v3-btn-ghost" type="button" onClick={runOcr} disabled={!!ocr}>
                                <ScanLine size={14} /> {ocr ? t('OCR running…') : t('Scan with OCR')}
                            </button>
                        )}
                        {(pdfTokens || ocrWordList) && (
                            <button className="v3-btn v3-btn-ghost" type="button" onClick={rescan} disabled={!!ocr || !!busy}>
                                {t('Re-scan')}
                            </button>
                        )}
                    </div>

                    {ocr && (
                        <div className="v3-import-progress">
                            <div className="v3-import-bar"><div style={{ width: `${Math.round((ocr.pct || 0) * 100)}%` }} /></div>
                            <span>{ocr.status} {Math.round((ocr.pct || 0) * 100)}%</span>
                        </div>
                    )}

                    {(pdfTokens || ocrWordList) && (
                        <div className="v3-import-tools">
                            <label>{t('Code pattern')}
                                <input type="text" value={codePattern} onChange={(e) => setCodePattern(e.target.value)} spellCheck={false} />
                            </label>
                            {ocrWordList && (
                                <label>{t('Min confidence')}: <b>{minConf}</b>
                                    <input type="range" min="0" max="90" step="5" value={minConf} onChange={(e) => setMinConf(+e.target.value)} />
                                </label>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Auto-place */}
            {csv && (
                <div className="v3-import-section">
                    <div className="v3-import-row">
                        <strong>{t('Auto-place unplaced rows')}</strong>
                    </div>
                    <div className="v3-import-actions">
                        <button className="v3-btn v3-btn-ghost" type="button" onClick={() => placeUnplaced('oval')}>
                            <Circle size={14} /> {t('Oval ring')}
                        </button>
                        <button className="v3-btn v3-btn-ghost" type="button" onClick={() => placeUnplaced('grid')}>
                            <Grid3x3 size={14} /> {t('Grid')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
